import { createContext, useContext, useState, useRef, useEffect, ReactNode } from "react";
import { toast } from "@/hooks/use-toast";

interface AudioContextType {
  // Playback state
  isPlaying: boolean;
  
  // Volume controls
  musicVolume: number;
  soundVolume: number;
  
  // ATC stream
  atcStreamUrl: string | null;
  isAtcActive: boolean;
  isAtcLoading: boolean;
  currentAirportCode: string | null;
  
  // Controls
  playPause: () => void;
  setMusicVolume: (volume: number) => void;
  setSoundVolume: (volume: number) => void;
  setAtcStream: (url: string | null, airportCode?: string) => Promise<void>;
  toggleAtc: () => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudio must be used within AudioProvider");
  }
  return context;
};

interface AudioProviderProps {
  children: ReactNode;
}

export const AudioProvider = ({ children }: AudioProviderProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [musicVolume, setMusicVolumeState] = useState(70);
  const [soundVolume, setSoundVolumeState] = useState(50);
  const [atcStreamUrl, setAtcStreamUrl] = useState<string | null>(null);
  const [isAtcActive, setIsAtcActive] = useState(false);
  const [isAtcLoading, setIsAtcLoading] = useState(false);
  const [currentAirportCode, setCurrentAirportCode] = useState<string | null>(null);
  const [airportFeeds, setAirportFeeds] = useState<string[]>([]);
  const [currentFeedIndex, setCurrentFeedIndex] = useState(0);
  
  const atcAudioRef = useRef<HTMLAudioElement | null>(null);
  const isPlayingRef = useRef(false);
  
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Handle visibility change to keep audio playing when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isPlayingRef.current) {
        const atcAudio = atcAudioRef.current;
        
        // Resume ATC if it was active and got paused
        if (atcAudio && isAtcActive && atcAudio.paused) {
          atcAudio.play().catch(err => console.log('Could not resume ATC:', err));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAtcActive]);

  // Update ATC volume when sound volume changes
  useEffect(() => {
    if (atcAudioRef.current) {
      const calculatedVolume = soundVolume / 100;
      if (isFinite(calculatedVolume)) {
        atcAudioRef.current.volume = calculatedVolume;
      }
    }
  }, [soundVolume]);

  const playPause = async () => {
    console.log('PlayPause clicked, current state:', isPlaying);
    if (isPlaying) {
      atcAudioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Auto-start ATC if not active yet
      if (!isAtcActive) {
        console.log('Auto-selecting active ATC feed...');
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: feeds } = await supabase
          .from('atc_feeds')
          .select('stream_url, airport_code, airport_name')
          .eq('is_active', true)
          .limit(20);
        
        if (feeds && feeds.length > 0) {
          const randomFeed = feeds[Math.floor(Math.random() * feeds.length)];
          console.log('Auto-selected', randomFeed.airport_code);
          await setAtcStream(randomFeed.stream_url, randomFeed.airport_code);
          setIsAtcActive(true);
          atcAudioRef.current?.play().catch(e => console.error('Error playing ATC:', e));
        }
      } else if (atcAudioRef.current) {
        atcAudioRef.current.play().catch(e => console.error('Error playing ATC:', e));
      }
      
      setIsPlaying(true);
    }
  };

  const setAtcStream = async (url: string | null, airportCode?: string) => {
    console.log('Setting ATC stream:', url, 'Airport:', airportCode);
    
    if (atcAudioRef.current) {
      atcAudioRef.current.pause();
      atcAudioRef.current = null;
    }
    
    setAtcStreamUrl(url);
    setIsAtcActive(false);
    setIsAtcLoading(false);
    
    // If airport code provided, fetch all available feeds for failover
    if (airportCode && airportCode !== currentAirportCode) {
      setCurrentAirportCode(airportCode);
      
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data: feeds } = await supabase
          .from('atc_feeds')
          .select('stream_url')
          .eq('airport_code', airportCode)
          .eq('is_active', true)
          .order('feed_type');
        
        const feedUrls = feeds?.map(f => f.stream_url) || [];
        setAirportFeeds(feedUrls);
        setCurrentFeedIndex(feedUrls.indexOf(url) || 0);
        console.log(`Loaded ${feedUrls.length} feeds for ${airportCode}`);
      } catch (error) {
        console.error('Failed to load airport feeds:', error);
        setAirportFeeds([url].filter(Boolean) as string[]);
      }
    }
    
    if (url) {
      setIsAtcLoading(true);
      // Proxy the stream through our edge function to handle CORS
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const proxiedUrl = `${supabaseUrl}/functions/v1/atc-proxy?url=${encodeURIComponent(url)}`;
      
      const audio = new Audio(proxiedUrl);
      audio.loop = false;
      audio.preload = "auto";
      const calculatedVolume = soundVolume / 100;
      if (isFinite(calculatedVolume)) {
        audio.volume = calculatedVolume;
      }
      
      // Enhanced reconnection logic for live streams with auto-failover
      let reconnectAttempts = 0;
      const maxReconnectAttempts = 5;
      const reconnectDelay = 2000;
      let reconnectTimeout: number | null = null;
      
      const tryNextFeed = async () => {
        if (airportFeeds.length > 1) {
          const nextIndex = (currentFeedIndex + 1) % airportFeeds.length;
          const nextUrl = airportFeeds[nextIndex];
          console.log(`Switching to backup feed: ${nextUrl}`);
          
          toast({
            title: "Switching to backup feed",
            description: "Attempting to connect to alternative stream...",
          });
          
          setCurrentFeedIndex(nextIndex);
          await setAtcStream(nextUrl, currentAirportCode || undefined);
          
          if (isAtcActive) {
            atcAudioRef.current?.play().catch(e => console.error('Error playing backup feed:', e));
          }
        }
      };
      
      const reconnect = () => {
        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
        
        reconnectAttempts++;
        
        if (reconnectAttempts >= maxReconnectAttempts) {
          console.log(`Max reconnection attempts (${maxReconnectAttempts}) reached, trying next feed...`);
          reconnectAttempts = 0;
          tryNextFeed();
          return;
        }
        
        console.log(`Attempting to reconnect ATC stream (attempt ${reconnectAttempts}/${maxReconnectAttempts})...`);
        
        reconnectTimeout = window.setTimeout(() => {
          audio.load();
          if (isAtcActive) {
            audio.play()
              .then(() => {
                console.log('ATC stream reconnected successfully');
                reconnectAttempts = 0;
              })
              .catch(e => {
                console.error('Reconnection failed:', e);
                reconnect();
              });
          }
        }, reconnectDelay);
      };
      
      audio.addEventListener('loadedmetadata', () => {
        console.log('ATC stream metadata loaded');
      });
      
      audio.addEventListener('playing', () => {
        console.log('ATC stream is now playing with audio');
        setIsAtcActive(true);
        setIsAtcLoading(false);
        reconnectAttempts = 0;
      });
      
      audio.addEventListener('pause', () => {
        console.log('ATC stream paused');
      });
      
      audio.addEventListener('error', (e) => {
        console.error('ATC stream error:', e, 'Network state:', audio.networkState, 'Ready state:', audio.readyState);
        setIsAtcLoading(false);
        reconnect();
      });
      
      audio.addEventListener('stalled', () => {
        console.warn('ATC stream stalled, attempting reconnection...');
        setIsAtcActive(false);
        reconnect();
      });
      
      audio.addEventListener('suspend', () => {
        console.warn('ATC stream suspended');
      });
      
      audio.addEventListener('waiting', () => {
        console.log('ATC stream buffering...');
        setIsAtcLoading(true);
      });
      
      audio.addEventListener('canplay', () => {
        console.log('ATC stream ready to play');
        setIsAtcLoading(false);
        reconnectAttempts = 0;
      });
      
      audio.addEventListener('ended', () => {
        console.log('ATC stream ended unexpectedly, reconnecting...');
        setIsAtcActive(false);
        reconnect();
      });
      
      atcAudioRef.current = audio;
      
      return new Promise<void>((resolve) => {
        const readyHandler = () => {
          console.log('ATC stream setup complete and ready');
          resolve();
        };
        
        audio.addEventListener('canplay', readyHandler, { once: true });
        
        audio.addEventListener('error', () => {
          console.log('ATC stream setup failed');
          resolve();
        }, { once: true });
        
        setTimeout(() => {
          console.log('ATC stream setup timeout, resolving anyway');
          resolve();
        }, 3000);
      });
    }
    
    return Promise.resolve();
  };

  const toggleAtc = () => {
    console.log('Toggle ATC, current state:', isAtcActive, 'audio ref:', !!atcAudioRef.current);
    if (!atcAudioRef.current) {
      console.warn('No ATC audio ref available');
      return;
    }
    
    if (isAtcActive) {
      atcAudioRef.current.pause();
      setIsAtcActive(false);
    } else {
      setIsAtcActive(true);
      if (!isPlaying) {
        setIsPlaying(true);
      }
      atcAudioRef.current.play()
        .then(() => console.log('ATC stream playing'))
        .catch(e => console.error('Error playing ATC:', e));
    }
  };

  const value: AudioContextType = {
    isPlaying,
    musicVolume,
    soundVolume,
    atcStreamUrl,
    isAtcActive,
    isAtcLoading,
    currentAirportCode,
    playPause,
    setMusicVolume: setMusicVolumeState,
    setSoundVolume: setSoundVolumeState,
    setAtcStream,
    toggleAtc,
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
